import { ReactNode, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import EmailInput from '../../EmailInput';
import PasswordInput from '../../PasswordInput';
import { TextButton } from '../../TextButton';
import Img from '../../../components/Img';
import googleLogo from '../../../assets/images/icons/google.svg';

import dataModel from '../../../models/data';
import spinnerOverlayModel from '../../../models/spinnerOverlay';

import './styles.scss';

interface Props {
  className?: string;
}

interface State {
  emailIsValid: boolean;
  passwordIsValid: boolean;
  password: string;
}

export default function AdminLoginForm({ className = '' }: Props): ReactNode {
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    emailIsValid: false,
    passwordIsValid: false,
    password: ''
  });

  const { password, emailIsValid, passwordIsValid } = state;
  const { email, formAuthenticityToken, env } = dataModel;

  const searchParams: string = window.location.search;

  const onEmailChange = (email: string, emailIsValid: boolean) => {
    dataModel.updateEmail(email);

    setState({ ...state, emailIsValid });
  };

  const onPasswordChange = (password: string, passwordIsValid: boolean) =>
    setState({ ...state, password, passwordIsValid });

  const notEmptryInputValidation = (value: string) => !!value;

  const showSpinnerOverlay = () => {
    spinnerOverlayModel.show();
  };

  const formContent = useMemo(() => {
    return env === 'development' ? (
      <>
        <EmailInput
          className="admin-login-form-input admin-login-form-input-first"
          title={t('AdminLoginForm.inputTitle.email')}
          value={email}
          onChangeText={onEmailChange}
          name="admin_user[email]"
        />
        <PasswordInput
          className="admin-login-form-input"
          title={t('AdminLoginForm.inputTitle.password')}
          value={password}
          onChangeText={onPasswordChange}
          validation={notEmptryInputValidation}
          name="admin_user[password]"
        />
        <TextButton
          big={true}
          disabled={!(emailIsValid && passwordIsValid)}
          text={t('AdminLoginForm.button')}
          type="submit"
          className="admin-login-form-button admin-login-form-input"
          onPress={showSpinnerOverlay}
        />
      </>
    ) : (
      <div>
        <a href="/admin/auth/google_oauth2" className="google-button">
          <Img className="google-logo" src={googleLogo} />
          Log In with Google
        </a>
      </div>
    );
  }, [env, email, password, emailIsValid, passwordIsValid]);

  return (
    <form
      className={`admin-login-form ${className}`}
      method="post"
      action={`/admin/login${searchParams}`}
      noValidate
    >
      <div className="admin-login-form-title">{t(`AdminLoginForm.title`)}</div>
      <input
        type="hidden"
        name="authenticity_token"
        value={formAuthenticityToken}
      />
      {formContent}
    </form>
  );
}
